import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Loader2,
  ArrowLeft,
  Download,
  Trash2,
  MessageCircle,
  CheckSquare,
  Paperclip,
  Square,
  FolderSync,
  Edit2,
  Save,
  X,
  Send,
  Phone,
  CheckCircle,
  AlertCircle,
  Ban,
  ChevronDown,
  ChevronUp,
  Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { managementService, PreCadastro, ToDo, Note } from '@/services/management.service';
import { userService } from '@/services/user.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PdfPrintService } from '@/services/pdf-print.service';

interface ExtendedPreCadastro extends PreCadastro {
  coordenadora_nome?: string;
  colaborador_nome?: string;
}

const PreCadastroDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Dados principais
  const [preCadastro, setPreCadastro] = useState<ExtendedPreCadastro | null>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [todos, setTodos] = useState<ToDo[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [coordenadoras, setCoordanadoras] = useState<any[]>([]);
  const [kits, setKits] = useState<any[]>([]);

  // Estados de carregamento
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [loadingTodos, setLoadingTodos] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [updatingTodo, setUpdatingTodo] = useState<number | null>(null);

  // Status modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState('');
  const [selectedCollaborator, setSelectedCollaborator] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedKit, setSelectedKit] = useState('');
  const [selectedCoordinator, setSelectedCoordinator] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Contract creation modal
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [dontDoContract, setDontDoContract] = useState(false);
  const [pendingApprovalData, setPendingApprovalData] = useState<{
    kitSelected: string;
    coordinatorSelected: string;
  } | null>(null);

  // Colaboradores list
  const [colaboradores, setColaboradores] = useState<any[]>([]);

  // Notes modal
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteMessage, setNewNoteMessage] = useState('');
  const [newNoteColor, setNewNoteColor] = useState('success');
  const [addingNote, setAddingNote] = useState(false);

  // Add Attachment modal
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [attachmentName, setAttachmentName] = useState('');
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [creatingAttachment, setCreatingAttachment] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState<string>('');
  const [approvingSignature, setApprovingSignature] = useState<number | null>(null);

  // Export PDF
  const [exportingPdf, setExportingPdf] = useState(false);
  const pdfPrintService = new PdfPrintService();

  // Edição de atributos
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [savingAttributes, setSavingAttributes] = useState(false);

  // Disparo manual WhatsApp
  const [dispatchConfirmOpen, setDispatchConfirmOpen] = useState(false);
  const [dispatchData, setDispatchData] = useState<{
    phoneNumber: string;
    referenceName: string;
    referenceType: string;
  } | null>(null);
  const [sendingDispatch, setSendingDispatch] = useState(false);

  // Fila WhatsApp
  const [whatsappQueue, setWhatsappQueue] = useState<any[]>([]);

  // Score / Pontuação
  const [scoreData, setScoreData] = useState<any>(null);
  const [scoreExpanded, setScoreExpanded] = useState(false);

  // Serasa
  const [editingSerasa, setEditingSerasa] = useState(false);
  const [serasaValues, setSerasaValues] = useState({
    serasaScore: '',
    serasaRestricoes: '',
    serasaChancePagamento: '',
  });
  const [savingSerasa, setSavingSerasa] = useState(false);

  useEffect(() => {
    const userDataString = localStorage.getItem('userlogged');
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUser(userData);
      loadAllData();
    } else {
      navigate('/authentication/side-login');
    }
  }, [id]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPreCadastro(),
        loadAttachments(),
        loadTodos(),
        loadNotes(),
        loadCoordanadoras(),
        loadKits(),
        loadWhatsAppQueue(),
        loadColaboradores(),
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadPreCadastro = async () => {
    try {
      if (!id) return;
      const data = await managementService.getPreCadastroById(Number(id));
      setPreCadastro(data as ExtendedPreCadastro);
      setScoreData((data as any)?.score || null);
    } catch (error) {
      console.error('Erro ao carregar pré-cadastro:', error);
      toast.error('Erro ao carregar pré-cadastro');
    }
  };

  const loadAttachments = async () => {
    setLoadingAttachments(true);
    try {
      if (!id) return;
      const data = await managementService.getAttachments(Number(id));
      setAttachments(data || []);
    } catch (error) {
      console.error('Erro ao carregar anexos:', error);
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const loadTodos = async () => {
    setLoadingTodos(true);
    try {
      if (!id) return;
      const data = await managementService.getTodos(Number(id));
      setTodos(data || []);
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
      setTodos([]);
    } finally {
      setLoadingTodos(false);
    }
  };

  const loadNotes = async () => {
    setLoadingNotes(true);
    try {
      if (!id) return;
      const data = await managementService.getNotes(Number(id));
      setNotes(data || []);
    } catch (error) {
      console.error('Erro ao carregar notas:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadCoordanadoras = async () => {
    try {
      const data = await userService.getCoordenadoras();
      setCoordanadoras(data || []);
    } catch (error) {
      console.error('Erro ao carregar coordenadoras:', error);
    }
  };

  const loadKits = async () => {
    try {
      const data = await managementService.getKits();
      setKits(data || []);
    } catch (error) {
      console.error('Erro ao carregar kits:', error);
    }
  };

  const loadColaboradores = async () => {
    try {
      const data = await userService.getColaboradores();
      setColaboradores(data || []);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
  };

  const loadWhatsAppQueue = async () => {
    try {
      if (!id) return;
      const data = await managementService.getWhatsAppQueue(Number(id));
      setWhatsappQueue(data || []);
    } catch (error) {
      console.error('Erro ao carregar fila WhatsApp:', error);
      setWhatsappQueue([]);
    }
  };

  const handleUpdateStatus = async () => {
    if (!preCadastro) return;

    // Validar "Iniciar Análise" (analisando)
    if (selectedNewStatus === 'analisando' && !selectedCollaborator) {
      toast.error('Selecione um colaborador para iniciar a análise');
      return;
    }

    if (selectedNewStatus === 'aprovado' && (!selectedKit || !selectedCoordinator)) {
      toast.error('Selecione um kit e uma coordenadora para aprovação');

      return;
    }

    if (selectedNewStatus === 'reprovado' && !rejectionReason.trim()) {
      toast.error('Informe o motivo da reprovação');
      return;
    }

    // Validar tarefas pendentes ao aprovar
    if (selectedNewStatus === 'aprovado') {
      const incompleteTodos = todos.find((todo) => todo.situacao === 'pendente');
      if (incompleteTodos) {
        toast.error('Você possui itens pendentes no check list, finalize todos para aprovar');
        return;
      }

      // Se for aprovação, salvar dados e abrir modal de contrato
      setPendingApprovalData({
        kitSelected: selectedKit,
        coordinatorSelected: selectedCoordinator,
      });
      setDontDoContract(false);
      setContractModalOpen(true);
      return;
    }

    setUpdatingStatus(true);
    try {
      if (selectedNewStatus === 'analisando') {
        // Iniciar análise com colaborador
        await managementService.startAnalysisPreCadastro(preCadastro.id, selectedCollaborator);
        toast.success('Análise iniciada com sucesso');
      } else if (selectedNewStatus === 'reprovado') {
        await managementService.reproveAnalysisPreCadastro(preCadastro.id, rejectionReason);
        toast.success('Pré-cadastro reprovado com sucesso');
      }

      await Promise.all([
        loadPreCadastro(),
        loadAttachments(),
        loadNotes()
      ]);
      setStatusModalOpen(false);
      setSelectedNewStatus('');
      setSelectedCollaborator('');
      setRejectionReason('');
      setSelectedKit('');
      setSelectedCoordinator('');
    } catch (error: any) {
      console.error('Erro ao atualizar status:', error);
      toast.error(error.message || 'Erro ao atualizar status');
    } finally {
      setUpdatingStatus(false);
    }
  }

  const handleConfirmApproval = async () => {
    if (!preCadastro || !pendingApprovalData) return;

    setUpdatingStatus(true);
    try {
      await managementService.approveAnalysisPreCadastro(
        preCadastro.id,
        pendingApprovalData.kitSelected,
        Number(pendingApprovalData.coordinatorSelected),
        dontDoContract
      );
      toast.success('Pré-cadastro aprovado com sucesso');

      await Promise.all([
        loadPreCadastro(),
        loadAttachments(),
        loadNotes()
      ]);
      setStatusModalOpen(false);
      setContractModalOpen(false);
      setSelectedNewStatus('');
      setSelectedCollaborator('');
      setRejectionReason('');
      setSelectedKit('');
      setSelectedCoordinator('');
      setPendingApprovalData(null);
      setDontDoContract(false);
    } catch (error: any) {
      console.error('Erro ao atualizar status:', error);
      toast.error(error.message || 'Erro ao atualizar status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleToggleTodo = async (todo: ToDo) => {
    setUpdatingTodo(todo.id);
    try {
      const newSituacao = todo.situacao === 'pendente' ? 'concluido' : 'pendente';
      await managementService.updateTodo(todo.id, newSituacao);
      await loadTodos();
      toast.success('Tarefa atualizada');
    } catch (error: any) {
      console.error('Erro ao atualizar tarefa:', error);
      toast.error('Erro ao atualizar tarefa');
    } finally {
      setUpdatingTodo(null);
    }
  };

  const handleManualDispatch = (phoneNumber: string, referenceType: string, referenceName: string) => {
    setDispatchData({
      phoneNumber,
      referenceName,
      referenceType,
    });
    setDispatchConfirmOpen(true);
  };

  const handleConfirmDispatch = async () => {
    if (!dispatchData || !preCadastro) return;

    setSendingDispatch(true);
    try {
      // Chamar endpoint para deletar da fila
      await managementService.sendWhatsAppQueue(dispatchData.phoneNumber);

      // Criar mensagem WhatsApp
      const cleanPhone = dispatchData.phoneNumber.replace(/\D/g, '');
      const message = `Olá, ${dispatchData.referenceName}! Sou da equipe X do Infinito.

A(o) ${preCadastro.fullName} acabou de preencher o pré-cadastro na nossa plataforma para iniciar a revenda de semijoias, e indicou seu número como contato de referência.
Gostaria de confirmar se você o(a) conhece e se pode nos ajudar a validar as informações fornecidas?
Agradecemos desde já pela sua colaboração!`;

      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/55${cleanPhone}?text=${encodedMessage}`;

      // Abrir WhatsApp em nova guia
      window.open(whatsappUrl, '_blank');

      toast.success('Mensagem removida da fila e WhatsApp aberto');
      setDispatchConfirmOpen(false);
      setDispatchData(null);
    } catch (error: any) {
      console.error('Erro ao enviar disparo:', error);
      toast.error(error.message || 'Erro ao enviar disparo');
    } finally {
      setSendingDispatch(false);
    }
  };

  const handleOpenWhatsApp = (phoneNumber: string) => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/55${cleanPhone}`;
    window.open(whatsappUrl, '_blank');
  };

  const getQueueStatusForPhone = (phoneNumber: string) => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    return whatsappQueue.find(q => q.phoneNumber === cleanPhone || q.phoneNumber === `+55${cleanPhone}`);
  };

  const getStatusBadgeConfig = (status: string) => {
    const configs: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      pendente: {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: <Clock className="w-4 h-4" />,
        label: 'Pendente'
      },
      enviado: {
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: <CheckCircle className="w-4 h-4" />,
        label: 'Enviado'
      },
      falha: {
        color: 'bg-red-100 text-red-800 border-red-300',
        icon: <XCircle className="w-4 h-4" />,
        label: 'Falha'
      },
      cancelado: {
        color: 'bg-gray-100 text-gray-800 border-gray-300',
        icon: <AlertCircle className="w-4 h-4" />,
        label: 'Cancelado'
      }
    };
    return configs[status] || configs.pendente;
  };

  const handleAddNote = async () => {
    if (!id) return;
    if (!newNoteTitle.trim() || !newNoteMessage.trim()) {
      toast.error('Preencha título e mensagem');
      return;
    }

    setAddingNote(true);
    try {
      await managementService.createNote(Number(id), {
        title: newNoteTitle,
        message: newNoteMessage,
        color: newNoteColor,
      });
      await loadNotes();
      setNewNoteTitle('');
      setNewNoteMessage('');
      setNewNoteColor('success');
      setNotesModalOpen(false);
      toast.success('Nota adicionada com sucesso');
    } catch (error: any) {
      console.error('Erro ao adicionar nota:', error);
      toast.error('Erro ao adicionar nota');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!confirm('Deseja deletar esta nota?')) return;

    try {
      await managementService.deleteNote(noteId);
      await loadNotes();
      toast.success('Nota deletada com sucesso');
    } catch (error: any) {
      console.error('Erro ao deletar nota:', error);
      toast.error('Erro ao deletar nota');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamanho máximo (ex: 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Arquivo muito grande. Máximo 50MB');
      return;
    }

    convertImageToBase64(file);
  };

  const convertImageToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result?.toString() || '';
        setAttachmentPreview(base64String);
        setAttachmentFile(file);
        resolve(base64String);
      };
      reader.onerror = (error) => {
        toast.error('Erro ao converter o arquivo para base64');
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const file = new File([blob], `imagem_${Date.now()}.${blob.type.split('/')[1]}`, {
            type: blob.type,
          });
          convertImageToBase64(file)
            .then(() => {
              // Sucesso - a preview é gerenciada por state, sem manipular DOM
              toast.success('Imagem colada com sucesso');
            })
            .catch((error) => {
              toast.error('Erro ao colar imagem');
              console.error('Erro ao colar imagem:', error);
            });
        }
        break;
      }
    }
  };

  const handleAddAttachment = async () => {
    if (!id) return;

    // Validações
    if (!attachmentName.trim()) {
      toast.error('Informe o nome do anexo');
      return;
    }

    if (!attachmentDescription.trim()) {
      toast.error('Informe a descrição do anexo');
      return;
    }

    if (!attachmentPreview) {
      toast.error('Selecione um arquivo');
      return;
    }

    setCreatingAttachment(true);
    try {
      const attachmentData = {
        nome: attachmentName,
        descricao: attachmentDescription,
        fileUploaded: attachmentFile?.name || '',
        imagem: attachmentPreview,
        fileType: attachmentFile?.type || '',
      };

      await managementService.createAttachment(Number(id), attachmentData);
      await loadAttachments();

      // Limpar formulário
      setAttachmentName('');
      setAttachmentDescription('');
      setAttachmentFile(null);
      setAttachmentPreview('');
      setAttachmentModalOpen(false);

      toast.success('Anexo adicionado com sucesso');
    } catch (error: any) {
      console.error('Erro ao adicionar anexo:', error);
      toast.error(error.message || 'Erro ao adicionar anexo');
    } finally {
      setCreatingAttachment(false);
    }
  };

  const resetAttachmentForm = () => {
    setAttachmentName('');
    setAttachmentDescription('');
    setAttachmentFile(null);
    setAttachmentPreview('');
  };

  const downloadAttachment = async (attachment: any) => {
    if (!attachment.path) {
      toast.error('Link de download não disponível');
      return;
    }
    if (attachment.path === 'clicksign') {
      try {
        const url = await managementService.getDownloadUrl(attachment);
        if (!url) {
          toast.error('Erro ao obter link de download');
          return;
        }
        window.open(url, '_blank');
      } catch {
        toast.error('Erro ao obter link de download');
      }
      return;
    }
    window.open(attachment.path, '_blank');
  };

  const approveSignatures = async (attachmentId: number) => {
    setApprovingSignature(attachmentId);
    try {
      await managementService.approveSignature(attachmentId);
      toast.success('Assinatura aprovada com sucesso');
      await loadAttachments();
    } catch (error: any) {
      console.error('Erro ao aprovar assinatura:', error);
      toast.error(error.message || 'Erro ao aprovar assinatura');
    } finally {
      setApprovingSignature(null);
    }
  };

  // Funções para edição de atributos
  const openEditSection = (sectionName: string, values: Record<string, string>) => {
    setEditingSection(sectionName);
    setEditingValues({ ...values });
  };

  const handleAttributeChange = (key: string, value: string) => {
    setEditingValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveAttributes = async () => {
    if (!preCadastro) return;
    setSavingAttributes(true);

    try {
      // Atualizar cada atributo que foi modificado
      const originalValues = {
        fullName: preCadastro.fullName || '',
        rgDocument: preCadastro.rgDocument || '',
        cpfDocument: preCadastro.cpfDocument || '',
        scholarLevel: preCadastro.scholarLevel || '',
        maritalStatus: preCadastro.maritalStatus || '',
        nationality: preCadastro.nationality || '',
        dateOfBirth: preCadastro.dateOfBirth || '',
        cityState: preCadastro.cityState || '',
        neighborhood: preCadastro.neighborhood || '',
        address: preCadastro.address || '',
        cep: preCadastro.cep || '',
        cpfRestrictions: preCadastro.cpfRestrictions || '',
        avalist: preCadastro.avalist || '',
        creditCard: preCadastro.creditCard || '',
        mothersName: preCadastro.mothersName || '',
        children: preCadastro.children || '',
        residence: preCadastro.residence || '',
        residenceTime: preCadastro.residenceTime || '',
        job: preCadastro.job || '',
        income: preCadastro.income || '',
        salesMotivation: preCadastro.salesMotivation || '',
        profitObjective: preCadastro.profitObjective || '',
        doubt: preCadastro.doubt || '',
        phone: preCadastro.phone || '',
        email: preCadastro.email || '',
        instagram: preCadastro.instagram || '',
        referenceNameOne: preCadastro.referenceNameOne || '',
        referencePhoneOne: preCadastro.referencePhoneOne || '',
        referenceNameTwo: preCadastro.referenceNameTwo || '',
        referencePhoneTwo: preCadastro.referencePhoneTwo || '',
        referenceComercial: preCadastro.referenceComercial || '',
        referenceComercialPhone: preCadastro.referenceComercialPhone || '',
        indicatedCode: preCadastro.indicatedCode || '',
        grauInteresse: preCadastro.grauInteresse || '',
        aprenderAplicativo: preCadastro.aprenderAplicativo || '',
        usoTecnologia: preCadastro.usoTecnologia || '',
      };

      // Atualizar atributos que foram alterados
      for (const [key, value] of Object.entries(editingValues)) {
        if (originalValues[key as keyof typeof originalValues] !== value) {
          await managementService.updatePreCadastroAttribute(preCadastro.id, key, value);
        }
      }

      await loadPreCadastro();
      setEditingSection(null);
      setEditingValues({});
      toast.success('Atributos atualizados com sucesso');
    } catch (error: any) {
      console.error('Erro ao atualizar atributos:', error);
      toast.error(error.message || 'Erro ao atualizar atributos');
    } finally {
      setSavingAttributes(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditingValues({});
  };

  const handleStartEditSerasa = () => {
    setSerasaValues({
      serasaScore: preCadastro?.serasaScore != null ? String(preCadastro.serasaScore) : '',
      serasaRestricoes: preCadastro?.serasaRestricoes != null ? String(preCadastro.serasaRestricoes) : '',
      serasaChancePagamento: preCadastro?.serasaChancePagamento != null ? String(preCadastro.serasaChancePagamento) : '',
    });
    setEditingSerasa(true);
  };

  const handleSaveSerasa = async () => {
    if (!preCadastro) return;
    setSavingSerasa(true);
    try {
      const updates: { key: string; value: number | null }[] = [
        { key: 'serasaScore', value: serasaValues.serasaScore !== '' ? parseInt(serasaValues.serasaScore, 10) : null },
        { key: 'serasaRestricoes', value: serasaValues.serasaRestricoes !== '' ? parseInt(serasaValues.serasaRestricoes, 10) : null },
        { key: 'serasaChancePagamento', value: serasaValues.serasaChancePagamento !== '' ? parseFloat(serasaValues.serasaChancePagamento) : null },
      ];

      for (const { key, value } of updates) {
        const currentValue = preCadastro[key as keyof typeof preCadastro];
        if (currentValue !== value) {
          await managementService.updatePreCadastroAttribute(preCadastro.id, key, value);
        }
      }

      await loadPreCadastro();
      setEditingSerasa(false);
      toast.success('Dados Serasa atualizados com sucesso');
    } catch (error: any) {
      console.error('Erro ao atualizar dados Serasa:', error);
      toast.error(error.message || 'Erro ao atualizar dados Serasa');
    } finally {
      setSavingSerasa(false);
    }
  };

  // Componente para renderizar uma seção com hover de edição
  const EditableSection = ({ 
    title, 
    sectionId, 
    children,
    values 
  }: { 
    title: string; 
    sectionId: string; 
    children: React.ReactNode;
    values: Record<string, string>;
  }) => {
    return (
      <div className="group relative">
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">• {title}</p>
          {children}
        </div>
        <button
          onClick={() => openEditSection(sectionId, values)}
          className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
          title="Editar"
        >
          <Edit2 className="w-4 h-4 text-blue-600" />
        </button>
      </div>
    );
  };

  const exportPdf = async () => {
    if (!preCadastro) return;

    setExportingPdf(true);

    try {
      // Buscar os anexos
      const attachmentsList = await managementService.getAttachments(Number(id));

      // Abrir janela de impressão
      pdfPrintService.openPrintWindow(preCadastro, attachmentsList || []);

      toast.success('Documento aberto para impressão');
    } catch (error) {
      console.error('Erro ao buscar anexos:', error);
      // Mesmo se der erro nos anexos, ainda assim gerar o PDF
      pdfPrintService.openPrintWindow(preCadastro, []);
      toast.success('Documento aberto para impressão (sem anexos)');
    } finally {
      setExportingPdf(false);
    }
  };

  const formatCPF = (cpf: string) => {
    const clean = cpf.replace(/\D/g, '');
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone: string) => {
    const clean = phone.replace(/\D/g, '');
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const getSituacaoBadgeColor = (situacao: string) => {
    const statusMap: Record<string, { color: string; label: string }> = {
      pendente: { color: 'bg-yellow-500 text-white', label: 'PENDENTE' },
      analisando: { color: 'bg-orange-500 text-white', label: 'ANÁLISE EM ANDAMENTO' },
      verificando_contrato: { color: 'bg-orange-500 text-white', label: 'VERIFICANDO CONTRATO' },
      aprovado: { color: 'bg-blue-600 text-white', label: 'APROVADO' },
      aguardando_assinaturas: { color: 'bg-blue-600 text-white', label: 'AGUARDANDO ASSINATURA & KIT' },
      concluido: { color: 'bg-green-600 text-white', label: 'CONCLUÍDO' },
      reprovado: { color: 'bg-red-600 text-white', label: 'REPROVADO' },
      deletado: { color: 'bg-red-600 text-white', label: 'REMOVIDO' },
    };
    return statusMap[situacao] || { color: 'bg-gray-500 text-white', label: situacao };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!preCadastro) {
    return (
      <div className="p-4 lg:p-8">
        <Button variant="ghost" onClick={() => navigate('/pre-cadastros')} className="gap-2 mb-4">
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Button>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Pré-cadastro não encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate('/pre-cadastros')} className="gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          {/* <h1 className="text-3xl font-bold tracking-tight">{preCadastro.fullName}</h1>
          <p className="text-muted-foreground mt-1">CPF: {formatCPF(preCadastro.cpfDocument)}</p> */}
        </div>
        <div className='flex items-center gap-2 '>
          <Button 
            onClick={exportPdf} 
            disabled={exportingPdf}
            size="sm" variant='secondary'
          >
            <Download className="w-4 h-4 mr-2" />
            {exportingPdf ? 'Aguarde...' : 'Download'}
          </Button>
          {/* <Badge className={getSituacaoBadgeColor(preCadastro.situacao).color}>
            {getSituacaoBadgeColor(preCadastro.situacao).label}
          </Badge> */}
          {preCadastro.situacao !== 'concluido' && (
            <Button
              disabled={preCadastro.situacao === 'deletado' || preCadastro.situacao === 'pendente'}
              onClick={() => setStatusModalOpen(true)} size="sm">
              <FolderSync className="w-4 h-4 mr-2" />
              Atualizar Status
            </Button>
          )}
        </div>
      </div>

      {/* Iniciar Análise - Mostrar se status for 'pendente' */}
      {preCadastro.situacao === 'pendente' && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-sm mb-2">Iniciar Análise</h3>
                <p className="text-sm text-muted-foreground">
                  Para começar, inicie o processo de análise informando o colaborador responsável.
                </p>
              </div>
              <Button 
                onClick={() => {
                  setSelectedNewStatus('analisando');
                  setStatusModalOpen(true);
                }}
                size="sm"
                className="whitespace-nowrap"
              >
                Iniciar Análise
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Score / Pontuação */}
      {scoreData && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5 text-yellow-500" />
                <h3 className="font-semibold text-sm">Pontuação do Pré-Cadastro</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setScoreExpanded(!scoreExpanded)}
              >
                {scoreExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {scoreExpanded ? 'Recolher' : 'Ver detalhes'}
              </Button>
            </div>

            <div className="flex items-center gap-6">
              {/* Score circular */}
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="3"
                  />
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke={scoreData.percentual >= 70 ? '#22c55e' : scoreData.percentual >= 40 ? '#eab308' : '#ef4444'}
                    strokeWidth="3"
                    strokeDasharray={`${scoreData.percentual}, 100`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold">{scoreData.pontuacaoTotal}</span>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl font-bold">{scoreData.pontuacaoTotal}</span>
                  <span className="text-sm text-muted-foreground">/ {scoreData.pontuacaoMaxima} pts</span>
                  <Badge className={
                    scoreData.percentual >= 70
                      ? 'bg-green-100 text-green-800 border-green-300'
                      : scoreData.percentual >= 40
                        ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                        : 'bg-red-100 text-red-800 border-red-300'
                  }>
                    {scoreData.percentual}%
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {scoreData.criteriosAtendidos} de {scoreData.totalCriterios} critérios atendidos
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      scoreData.percentual >= 70
                        ? 'bg-green-500'
                        : scoreData.percentual >= 40
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.max(0, Math.min(100, scoreData.percentual))}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Detalhes expandíveis */}
            {scoreExpanded && scoreData.detalhes && (
              <div className="mt-6 border-t pt-4">
                {/* Inputs Serasa inline */}
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-800">Dados Serasa</span>
                    </div>
                    {!editingSerasa ? (
                      <Button variant="ghost" size="sm" onClick={handleStartEditSerasa}>
                        <Edit2 className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingSerasa(false)} disabled={savingSerasa}>
                          <X className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                        <Button size="sm" onClick={handleSaveSerasa} disabled={savingSerasa}>
                          {savingSerasa ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Salvar
                        </Button>
                      </div>
                    )}
                  </div>
                  {!editingSerasa ? (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Score de Crédito</p>
                        <p className={`text-lg font-bold ${preCadastro.serasaScore != null ? (preCadastro.serasaScore >= 700 ? 'text-green-600' : preCadastro.serasaScore >= 500 ? 'text-yellow-600' : preCadastro.serasaScore >= 300 ? 'text-orange-600' : 'text-red-600') : 'text-muted-foreground'}`}>
                          {preCadastro.serasaScore != null ? `${preCadastro.serasaScore} / 1000` : 'Não informado'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Restrições Ativas</p>
                        <p className={`text-lg font-bold ${preCadastro.serasaRestricoes != null ? (preCadastro.serasaRestricoes === 0 ? 'text-green-600' : preCadastro.serasaRestricoes <= 2 ? 'text-yellow-600' : 'text-red-600') : 'text-muted-foreground'}`}>
                          {preCadastro.serasaRestricoes != null ? preCadastro.serasaRestricoes : 'Não informado'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Chance de Pagamento</p>
                        <p className={`text-lg font-bold ${preCadastro.serasaChancePagamento != null ? (preCadastro.serasaChancePagamento >= 70 ? 'text-green-600' : preCadastro.serasaChancePagamento >= 50 ? 'text-yellow-600' : 'text-red-600') : 'text-muted-foreground'}`}>
                          {preCadastro.serasaChancePagamento != null ? `${preCadastro.serasaChancePagamento}%` : 'Não informado'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <Label htmlFor="serasaScore" className="text-xs">Score (0 - 1000)</Label>
                        <Input id="serasaScore" type="number" min={0} max={1000} placeholder="Ex: 750" value={serasaValues.serasaScore} onChange={(e) => setSerasaValues(prev => ({ ...prev, serasaScore: e.target.value }))} />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="serasaRestricoes" className="text-xs">Restrições Ativas</Label>
                        <Input id="serasaRestricoes" type="number" min={0} placeholder="Ex: 0" value={serasaValues.serasaRestricoes} onChange={(e) => setSerasaValues(prev => ({ ...prev, serasaRestricoes: e.target.value }))} />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="serasaChancePagamento" className="text-xs">Chance de Pagamento (%)</Label>
                        <Input id="serasaChancePagamento" type="number" min={0} max={100} step="0.1" placeholder="Ex: 85.5" value={serasaValues.serasaChancePagamento} onChange={(e) => setSerasaValues(prev => ({ ...prev, serasaChancePagamento: e.target.value }))} />
                      </div>
                    </div>
                  )}
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Critério</TableHead>
                      <TableHead>Pergunta</TableHead>
                      <TableHead>Resposta</TableHead>
                      <TableHead className="text-right">Pontos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scoreData.detalhes.map((d: any, i: number) => (
                      <TableRow key={i} className={!d.match ? 'opacity-50' : ''}>
                        <TableCell className="font-medium capitalize text-xs">
                          {d.criterio.replace(/_/g, ' ')}
                        </TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate">
                          {d.pergunta}
                        </TableCell>
                        <TableCell className="text-xs">
                          {d.match ? d.resposta_label : d.resposta_usuario}
                        </TableCell>
                        <TableCell className={`text-right text-xs font-bold ${
                          d.pontos > 0 ? 'text-green-600' : d.pontos < 0 ? 'text-red-600' : 'text-gray-400'
                        }`}>
                          {d.pontos > 0 ? '+' : ''}{d.pontos}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dados Pessoais */}
      <Card>
        <CardContent className="pt-6">
          {/* Primeira Linha */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            {/* Nome Completo */}
            <EditableSection
              title="Nome Completo"
              sectionId="nomeCompleto"
              values={{
                fullName: preCadastro.fullName || '',
              }}
            >
              <p className="text-lg font-bold">{preCadastro.fullName}</p>
            </EditableSection>

            {/* Documentos */}
            <EditableSection
              title="Documentos"
              sectionId="documentos"
              values={{
                rgDocument: preCadastro.rgDocument || '',
                cpfDocument: preCadastro.cpfDocument || '',
              }}
            >
              <p className="text-sm">
                RG: <span className="font-bold">{preCadastro.rgDocument || '-'}</span>
                <br />
                CPF: <span className="font-bold">{formatCPF(preCadastro.cpfDocument)}</span>
              </p>
            </EditableSection>

            {/* Status */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">• Status</p>
              <Badge className={getSituacaoBadgeColor(preCadastro.situacao).color}>
                {getSituacaoBadgeColor(preCadastro.situacao).label}
              </Badge>
            </div>
          </div>

          <hr className="my-4 border-muted" />

          {/* Segunda Linha */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            {/* Contatos & Sociais */}
            <EditableSection
              title="Contatos & Sociais"
              sectionId="contatosSociais"
              values={{
                phone: preCadastro.phone || '',
                email: preCadastro.email || '',
                instagram: preCadastro.instagram || '',
              }}
            >
              <p className="text-sm">
                Telefone: <span className="font-bold">{formatPhone(preCadastro.phone)}</span>
                <br />
                E-mail: <span className="font-bold">{preCadastro.email}</span>
                <br />
                Instagram: <span className="font-bold">{preCadastro.instagram || '-'}</span>
              </p>
            </EditableSection>

            {/* Dados Cívicos */}
            <EditableSection
              title="Dados Cívicos"
              sectionId="dadosCivicos"
              values={{
                scholarLevel: preCadastro.scholarLevel || '',
                dateOfBirth: preCadastro.dateOfBirth || '',
                maritalStatus: preCadastro.maritalStatus || '',
                nationality: preCadastro.nationality || '',
              }}
            >
              <p className="text-sm">
                Escolaridade: <span className="font-bold">{preCadastro.scholarLevel || '-'}</span>
                <br />
                Nasc.: <span className="font-bold">
                  {preCadastro.dateOfBirth?.length === 8
                    ? `${preCadastro.dateOfBirth.substring(0, 2)}/${preCadastro.dateOfBirth.substring(2, 4)}/${preCadastro.dateOfBirth.substring(4, 8)}`
                    : preCadastro.dateOfBirth || '-'}
                </span>
                {' | '}
                Estado Civil: <span className="font-bold">{preCadastro.maritalStatus || '-'}</span>
                <br />
                Nacionalidade: <span className="font-bold">{preCadastro.nationality || '-'}</span>
              </p>
            </EditableSection>

            {/* Localidade */}
            <EditableSection
              title="Localidade"
              sectionId="localidade"
              values={{
                cityState: preCadastro.cityState || '',
                neighborhood: preCadastro.neighborhood || '',
                address: preCadastro.address || '',
                cep: preCadastro.cep || '',
              }}
            >
              <p className="text-sm">
                Cidade/Estado: <span className="font-bold">{preCadastro.cityState || '-'}</span>
                {' | '}
                Bairro: <span className="font-bold">{preCadastro.neighborhood || '-'}</span>
                <br />
                Endereço: <span className="font-bold">{preCadastro.address || '-'}</span>
                <br />
                CEP: <span className="font-bold">{preCadastro.cep || '-'}</span>
              </p>
            </EditableSection>
          </div>

          <hr className="my-4 border-muted" />

          {/* Terceira Linha */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            {/* Restrições & Crédito */}
            <EditableSection
              title="Restrições & Crédito"
              sectionId="restricoesCredito"
              values={{
                cpfRestrictions: preCadastro.cpfRestrictions || '',
                avalist: preCadastro.avalist || '',
                creditCard: preCadastro.creditCard || '',
              }}
            >
              <p className="text-sm">
                Restrição: <span className="font-bold">{preCadastro.cpfRestrictions || '-'}</span>
                <br />
                Comprometimento manter os acertos: <span className="font-bold">{preCadastro.avalist || '-'}</span>
                <br />
                Cartão de Crédito: <span className="font-bold">{preCadastro.creditCard || '-'}</span>
              </p>
            </EditableSection>

            {/* Parentalidade */}
            <EditableSection
              title="Parentalidade"
              sectionId="parentalidade"
              values={{
                mothersName: preCadastro.mothersName || '',
                children: preCadastro.children || '',
              }}
            >
              <p className="text-sm">
                Nome da Mãe: <span className="font-bold">{preCadastro.mothersName || '-'}</span>
                <br />
                Filhos: <span className="font-bold">{preCadastro.children || '-'}</span>
              </p>
            </EditableSection>

            {/* Residência & Profissão */}
            <EditableSection
              title="Residência & Profissão"
              sectionId="residenciaProfissao"
              values={{
                residence: preCadastro.residence || '',
                residenceTime: preCadastro.residenceTime || '',
                job: preCadastro.job || '',
                income: preCadastro.income || '',
              }}
            >
              <p className="text-sm">
                Moradia: <span className="font-bold">{preCadastro.residence || '-'}</span>
                {' | '}
                Período: <span className="font-bold">{preCadastro.residenceTime || '-'}</span>
                <br />
                Profissão: <span className="font-bold">{preCadastro.job || '-'}</span>
                <br />
                Renda Mensal: <span className="font-bold">{preCadastro.income || '-'}</span>
                <br />
                Comprovante: <span className="font-bold">{preCadastro.residenceProof || '-'}</span>
              </p>
            </EditableSection>
          </div>

          <hr className="my-4 border-muted" />

          {/* Quarta Linha */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            {/* Motivação */}
            <EditableSection
              title="Motivação p/vender"
              sectionId="motivacao"
              values={{
                salesMotivation: preCadastro.salesMotivation || '',
              }}
            >
              <p className="text-sm">{preCadastro.salesMotivation || '-'}</p>
            </EditableSection>

            {/* Objetivo de Lucro */}
            <EditableSection
              title="Objetivo de Lucro/mês"
              sectionId="objetivoLucro"
              values={{
                profitObjective: preCadastro.profitObjective || '',
              }}
            >
              <p className="text-sm">{preCadastro.profitObjective || '-'}</p>
            </EditableSection>

            {/* Dúvidas */}
            <EditableSection
              title="Dúvidas"
              sectionId="duvidas"
              values={{
                doubt: preCadastro.doubt || '',
              }}
            >
              <p className="text-sm">{preCadastro.doubt || '-'}</p>
            </EditableSection>
          </div>

          <hr className="my-4 border-muted" />

          {/* Quinta Linha - Referências */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            {/* 1ª Referência */}
            <EditableSection
              title="1ª Referência"
              sectionId="referencia1"
              values={{
                referenceNameOne: preCadastro.referenceNameOne || '',
                referencePhoneOne: preCadastro.referencePhoneOne || '',
              }}
            >
              <p className="text-sm">
                <span className="font-bold">{preCadastro.referenceNameOne || '-'}</span>
                <br />
                <span className="font-bold">
                  {preCadastro.referencePhoneOne || '-'}
                </span>
              </p>
              {preCadastro.referencePhoneOne && (
                <div className="flex gap-2 mt-3">
                  {(() => {
                    const queueStatus = getQueueStatusForPhone(preCadastro.referencePhoneOne || '');
                    const statusConfig = queueStatus ? getStatusBadgeConfig(queueStatus.status) : null;
                    return (
                      <>
                        {statusConfig ? (
                          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${statusConfig.color}`}>
                            {statusConfig.icon}
                            {statusConfig.label}
                          </div>
                        ): (
                          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border bg-gray-100 text-gray-800 border-gray-300`}>
                            <Ban className="w-4 h-4" />
                            Fora da fila
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleManualDispatch(preCadastro.referencePhoneOne || '', '1ª Referência', preCadastro.referenceNameOne || 'Referência')}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Disparar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleOpenWhatsApp(preCadastro.referencePhoneOne || '')}
                        >
                          <Phone className="w-3 h-3 mr-1" />
                          Contato
                        </Button>
                      </>
                    );
                  })()}
                </div>
              )}
            </EditableSection>

            {/* 2ª Referência */}
            <EditableSection
              title="2ª Referência"
              sectionId="referencia2"
              values={{
                referenceNameTwo: preCadastro.referenceNameTwo || '',
                referencePhoneTwo: preCadastro.referencePhoneTwo || '',
              }}
            >
              <p className="text-sm">
                <span className="font-bold">{preCadastro.referenceNameTwo || '-'}</span>
                <br />
                <span className="font-bold">
                  {preCadastro.referencePhoneTwo || '-'}
                </span>
              </p>
              {preCadastro.referencePhoneTwo && (
                <div className="flex gap-2 mt-3">
                  {(() => {
                    const queueStatus = getQueueStatusForPhone(preCadastro.referencePhoneTwo || '');
                    const statusConfig = queueStatus ? getStatusBadgeConfig(queueStatus.status) : null;
                    return (
                      <>
                       {statusConfig ? (
                          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${statusConfig.color}`}>
                            {statusConfig.icon}
                            {statusConfig.label}
                          </div>
                        ): (
                          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border bg-gray-100 text-gray-800 border-gray-300`}>
                            <Ban className="w-4 h-4" />
                            Fora da fila
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleManualDispatch(preCadastro.referencePhoneTwo || '', '2ª Referência', preCadastro.referenceNameTwo || 'Referência')}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Disparar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleOpenWhatsApp(preCadastro.referencePhoneTwo || '')}
                        >
                          <Phone className="w-3 h-3 mr-1" />
                          Contato
                        </Button>
                      </>
                    );
                  })()}
                </div>
              )}
            </EditableSection>

            {/* Referência Comercial */}
            <EditableSection
              title="Referência Comercial"
              sectionId="referenciaComercial"
              values={{
                referenceComercial: preCadastro.referenceComercial || '',
                referenceComercialPhone: preCadastro.referenceComercialPhone || '',
              }}
            >
              <p className="text-sm">
                <span className="font-bold">{preCadastro.referenceComercial || '-'}</span>
                <br />
                <span className="font-bold">
                  {preCadastro.referenceComercialPhone || '-'}
                </span>
              </p>
              {preCadastro.referenceComercialPhone && (
                <div className="flex gap-2 mt-3">
                  {(() => {
                    const queueStatus = getQueueStatusForPhone(preCadastro.referenceComercialPhone || '');
                    const statusConfig = queueStatus ? getStatusBadgeConfig(queueStatus.status) : null;
                    return (
                      <>
                        {statusConfig ? (
                          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${statusConfig.color}`}>
                            {statusConfig.icon}
                            {statusConfig.label}
                          </div>
                        ): (
                          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border bg-gray-100 text-gray-800 border-gray-300`}>
                            <Ban className="w-4 h-4" />
                            Fora da fila
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleManualDispatch(preCadastro.referenceComercialPhone || '', 'Referência Comercial', preCadastro.referenceComercial || 'Referência')}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Disparar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleOpenWhatsApp(preCadastro.referenceComercialPhone || '')}
                        >
                          <Phone className="w-3 h-3 mr-1" />
                          Contato
                        </Button>
                      </>
                    );
                  })()}
                </div>
              )}
            </EditableSection>
          </div>

          <hr className="my-4 border-muted" />

          {/* Sexta Linha - Indicador e Interesse */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            {/* Indicador */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">• Indicador</p>
              <p className="text-sm font-bold">
                {preCadastro.indicatedCode
                  ? `#${preCadastro.indicatedCode} ${preCadastro.indicador_nome} (${preCadastro.indicador_role})`
                  : '---'}
              </p>
            </div>

            {/* Interesse utilizar Xizinha */}
            <EditableSection
              title="Interesse utilizar Xizinha"
              sectionId="interesseXizinha"
              values={{
                grauInteresse: preCadastro.grauInteresse || '',
              }}
            >
              <p className="text-sm font-bold">{preCadastro.grauInteresse || '---'}</p>
            </EditableSection>

            {/* Aprendizado utilização do aplicativo */}
            <EditableSection
              title="Aprendizado utilização do aplicativo"
              sectionId="aprendizagemAplicativo"
              values={{
                aprenderAplicativo: preCadastro.aprenderAplicativo || '',
              }}
            >
              <p className="text-sm font-bold">{preCadastro.aprenderAplicativo || '---'}</p>
            </EditableSection>
          </div>

          <hr className="my-4 border-muted" />

          {/* Sétima Linha - Tecnologia, Contrato e Embaixadora */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            {/* Uso de tecnologia/redes sociais */}
            <EditableSection
              title="Uso de tecnologia/redes sociais"
              sectionId="usoTecnologia"
              values={{
                usoTecnologia: preCadastro.usoTecnologia || '',
              }}
            >
              <p className="text-sm font-bold">{preCadastro.usoTecnologia || '---'}</p>
            </EditableSection>

            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">• Acordo Contrato</p>
              <p className="text-sm font-bold">{preCadastro.acordoContrato || '---'}</p>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">• Motivo Embaixador</p>
              <p className="text-sm font-bold">{preCadastro.ambassadorReason || '---'}</p>
            </div>
          </div>

          <hr className="my-4 border-muted" />

          {/* Oitava Linha - Veículo, Entrevista e Treinamento */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">• Veículo</p>
              <p className="text-sm font-bold">{preCadastro.veiculo || '---'}</p>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">• Entrevista Online</p>
              <p className="text-sm font-bold">{preCadastro.disponivelEntrevistaOnline || '---'}</p>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">• Treinamento Vendas</p>
              <p className="text-sm font-bold">{preCadastro.disponivelTreinamentoVendas || '---'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs - Anexos, Tarefas, Comentários */}
      <Tabs defaultValue="attachments" className="w-full">
        <TabsList>
          <TabsTrigger value="attachments" className="gap-2">
            <Paperclip className="w-4 h-4" />
            Anexos ({attachments.length})
          </TabsTrigger>
          <TabsTrigger value="todos" className="gap-2">
            <CheckSquare className="w-4 h-4" />
            Tarefas ({todos.filter(t => t.situacao === 'concluido').length}/{todos.length})
          </TabsTrigger>
          <TabsTrigger value="notes" className="gap-2">
            <MessageCircle className="w-4 h-4" />
            Comentários ({notes.length})
          </TabsTrigger>
        </TabsList>

        {/* Anexos Tab */}
        <TabsContent value="attachments">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Anexos</CardTitle>
                  <CardDescription>Documentos e contratos anexados ao pré-cadastro</CardDescription>
                </div>
                <Button size="sm" onClick={() => setAttachmentModalOpen(true)}>
                  Adicionar Anexo
                </Button>
              </div>
            </CardHeader>
          </Card>

          <Card className="mt-4">
            <CardContent className="pt-6">
              {loadingAttachments ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : attachments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Nenhum anexo disponível</p>
              ) : (
                <div className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Id</TableHead>
                        <TableHead className="w-40">Nome</TableHead>
                        <TableHead className="w-48">Link Externo</TableHead>
                        <TableHead className="w-16 text-center">Download</TableHead>
                        <TableHead className="w-32">Tipo</TableHead>
                        <TableHead className="w-40">Situação</TableHead>
                        <TableHead className="w-20 text-center">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attachments.map((attachment) => (
                        <TableRow key={attachment.id} className="align-top">
                          <TableCell className="text-sm font-medium">{attachment.id}</TableCell>
                          <TableCell className="text-sm">
                            <div className="break-words">
                              <p className="font-medium">{attachment.fileUploaded}</p>
                              <p className="text-xs break-words font-bold">
                                {attachment.name}<br/>
                                <span className='text-muted-foreground'>
                                  {attachment.description ? attachment.description : '-'}
                                </span>
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {attachment.url && attachment.url.includes('http') ? (
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline break-all"
                              >
                                {attachment.url}
                              </a>
                            ) : attachment.attachmentType === 'contrato' && attachment.url?.includes('|') ? (
                              <a
                                href={`https://app.clicksign.com/accounts/1124274/folders/234262685/documents/${attachment.url.split('|')[1]}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline break-all"
                              >
                                {`https://app.clicksign.com/accounts/1124274/folders/234262685/documents/${attachment.url.split('|')[1]}`}
                              </a>
                            ) : (
                              <span className="text-muted-foreground">--</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {attachment.attachmentType === 'contrato' && attachment.path ? (
                              <button
                                onClick={() => downloadAttachment(attachment)}
                                className="text-blue-600 hover:text-blue-700 inline-flex"
                                title="Baixar contrato"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            ) : (
                              <span className="text-muted-foreground">--</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {attachment.attachmentType === 'contrato' ? (
                              <Badge variant="outline" className="break-words">Contrato Online</Badge>
                            ) : attachment.attachmentType === 'recibo_kit' ? (
                              <Badge variant="outline" className="break-words">Kit Maleta</Badge>
                            ) : attachment.attachmentType === 'outros' ? (
                              <Badge variant="outline" className="break-words">Outros</Badge>
                            ) : (
                              <Badge variant="outline" className="break-words">{attachment.attachmentType}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {attachment.attachmentType !== 'outros' ? (
                              attachment.situacao === 'aguardando_assinaturas' ? (
                              <Badge className="bg-blue-500 text-white break-words">Aguardando Assinaturas</Badge>
                              ) : attachment.situacao === 'verificando_assinatura' ? (
                              <Badge className="bg-yellow-500 text-white break-words">Verificando Assinatura</Badge>
                              ) : attachment.situacao === 'assinado' ? (
                              <Badge className="bg-blue-500 text-white break-words">Assinado</Badge>
                              ) : attachment.situacao === 'aguardando_pagamento' ? (
                              <Badge className="bg-blue-500 text-white break-words">Aguardando Pagamento</Badge>
                              ) : attachment.situacao === 'pago' ? (
                              <Badge className="bg-blue-500 text-white break-words">Pago</Badge>
                              ) : (
                              <Badge variant="outline" className="break-words">{attachment.situacao}</Badge>
                              )
                            ) : (
                              <span className="text-muted-foreground">--</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {attachment.situacao === 'verificando_assinatura' ? (
                              <button
                                onClick={() => approveSignatures(attachment.id)}
                                disabled={approvingSignature === attachment.id}
                                className="text-green-600 hover:text-green-700 inline-flex disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Aprovar assinaturas"
                              >
                                {approvingSignature === attachment.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4" />
                                )}
                              </button>
                            ) : attachment.attachmentType !== 'outros' ? (
                              <span className="text-muted-foreground">--</span>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tarefas Tab */}
        <TabsContent value="todos">
          <Card>
            <CardHeader>
              <CardTitle>Check List</CardTitle>
              <CardDescription>Tarefas de análise do pré-cadastro</CardDescription>
            </CardHeader>
            <CardContent>
              {todos.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Nenhuma tarefa encontrada</p>
              ) : (
                <div className="space-y-3">
                  {todos.map((todo) => (
                    <div
                      key={todo.id}
                      className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent transition-colors"
                    >
                      <button
                        onClick={() => handleToggleTodo(todo)}
                        disabled={updatingTodo === todo.id}
                        className="mt-1 disabled:opacity-50"
                      >
                        {updatingTodo === todo.id ? (
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        ) : todo.situacao === 'concluido' ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <Square className="w-5 h-5 text-yellow-600" />
                        )}
                      </button>
                      <div className="flex-1">
                        <p className={`font-medium ${todo.situacao === 'concluido' ? 'line-through text-muted-foreground' : ''}`}>
                          {todo.title}
                        </p>
                        <p className="text-sm text-muted-foreground">{todo.description}</p>
                        {todo.concludedAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Concluído em <strong className='text-gray-400'>{format(new Date(todo.concludedAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })} • {todo.concludedBy}</strong>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comentários Tab */}
        <TabsContent value="notes">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Comentários</CardTitle>
                  <CardDescription>Notas e observações sobre o pré-cadastro</CardDescription>
                </div>
                <Button onClick={() => setNotesModalOpen(true)} size="sm">
                  Adicionar Comentário
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {notes.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Nenhum comentário encontrado</p>
              ) : (
                <div className="space-y-3">
                  {notes.map((note) => {
                    const colorMap: Record<string, string> = {
                      primary: '#5d87ff',
                      warning: '#ffae1f',
                      accent: '#49beff',
                      error: '#fa896b',
                      success: '#13deb9',
                    };
                    const borderColor = colorMap[note.color] || '#5d87ff';
                    
                    return (
                      <div
                        key={note.id}
                        className="p-4 border rounded-lg space-y-2"
                        style={{
                          borderLeftColor: borderColor,
                          borderLeftWidth: '4px',
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: borderColor }}
                            />
                            <h4 className="font-medium">{note.title}</h4>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            {loadingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">{note.message}</p>
                        <p className="text-xs text-muted-foreground">
                          Por <strong>{note.autor}</strong> em <strong>{format(new Date(note.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</strong>
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Status Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Atualizar Status</DialogTitle>
            <DialogDescription>Selecione um novo status para este pré-cadastro</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="status-select">Novo Status</Label>
              <Select value={selectedNewStatus} onValueChange={setSelectedNewStatus}>
                <SelectTrigger id="status-select">
                  <SelectValue placeholder="Selecione um status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="analisando">Analisando</SelectItem>
                  <SelectItem value="aprovado">Aprovado</SelectItem>
                  <SelectItem value="reprovado">Reprovado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedNewStatus === 'analisando' && (
              <div>
                <Label htmlFor="collaborator-select">Colaborador Responsável</Label>
                <Select value={selectedCollaborator} onValueChange={setSelectedCollaborator}>
                  <SelectTrigger id="collaborator-select">
                    <SelectValue placeholder="Selecione um colaborador" />
                  </SelectTrigger>
                  <SelectContent>
                    {colaboradores.map((collab) => (
                      <SelectItem key={collab.codigo} value={collab.nome_razao_social}>
                        #{collab.codigo} - {collab.nome_razao_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedNewStatus === 'reprovado' && (
              <div>
                <Label htmlFor="reason">Motivo da Reprovação</Label>
                <Textarea
                  id="reason"
                  placeholder="Informe o motivo..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            )}

            {selectedNewStatus === 'aprovado' && (
              <>
                <div>
                  <Label htmlFor="kit-select">Kit</Label>
                  <Select value={selectedKit} onValueChange={setSelectedKit}>
                    <SelectTrigger id="kit-select">
                      <SelectValue placeholder="Selecione um kit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="0" value="0">
                        Nenhum KIT
                      </SelectItem>
                      {/* <SelectItem key="1" value="1">
                        Disponiblizar seleção de KIT
                      </SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="coord-select">Coordenadora</Label>
                  <Select value={selectedCoordinator} onValueChange={setSelectedCoordinator}>
                    <SelectTrigger id="coord-select">
                      <SelectValue placeholder="Selecione uma coordenadora" />
                    </SelectTrigger>
                    <SelectContent>
                      {coordenadoras.map((coord: any) => (
                        <SelectItem key={coord.id} value={coord.id.toString()}>
                          {coord.nome_razao_social || coord.fullName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="flex gap-2 pt-4">
              <Button onClick={handleUpdateStatus} disabled={updatingStatus}>
                {updatingStatus && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirmar
              </Button>
              <Button variant="outline" onClick={() => setStatusModalOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={notesModalOpen} onOpenChange={setNotesModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Comentário</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="note-title">Título</Label>
              <Input
                id="note-title"
                placeholder="Título do comentário"
                value={newNoteTitle}
                onChange={(e) => setNewNoteTitle(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="note-message">Mensagem</Label>
              <Textarea
                id="note-message"
                placeholder="Conteúdo do comentário"
                value={newNoteMessage}
                onChange={(e) => setNewNoteMessage(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div>
              <Label className="mb-3 block">Cor</Label>
              <div className="flex gap-3">
                {[
                  { value: 'primary', color: '#5d87ff', label: 'Azul' },
                  { value: 'warning', color: '#ffae1f', label: 'Amarelo' },
                  { value: 'accent', color: '#49beff', label: 'Cyan' },
                  { value: 'error', color: '#fa896b', label: 'Laranja' },
                  { value: 'success', color: '#13deb9', label: 'Verde' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setNewNoteColor(option.value)}
                    className={`w-8 h-8 rounded-full transition-all ${
                      newNoteColor === option.value
                        ? 'ring-2 ring-offset-2 ring-offset-background ring-foreground scale-110'
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: option.color }}
                    title={option.label}
                  />
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={handleAddNote} disabled={addingNote}>
                {addingNote && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Adicionar
              </Button>
              <Button variant="outline" onClick={() => setNotesModalOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Attachment Modal */}
      <Dialog open={attachmentModalOpen} onOpenChange={(open) => {
        setAttachmentModalOpen(open);
        if (!open) resetAttachmentForm();
      }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Nome do Anexo */}
            <div>
              <Label htmlFor="attach-name">Nome <span className="text-red-500">*</span></Label>
              <Input
                id="attach-name"
                placeholder="Digite aqui o nome"
                value={attachmentName}
                onChange={(e) => setAttachmentName(e.target.value.substring(0, 85))}
                maxLength={85}
              />
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="attach-description">Descrição <span className="text-red-500">*</span></Label>
              <Input
                id="attach-description"
                placeholder="Digite aqui uma breve descrição"
                value={attachmentDescription}
                onChange={(e) => setAttachmentDescription(e.target.value.substring(0, 150))}
                maxLength={150}
              />
            </div>

            {/* Upload de Arquivo */}
            <div className="border border-gray-400 rounded-lg p-3 space-y-2">
              <Label htmlFor="attach-file">
                Adicionar Arquivo <span className="text-red-500">*</span>
              </Label>
              <input
                id="attach-file"
                type="file"
                onChange={handleFileChange}
                className="w-full text-sm"
              />
              {/* Preview de Imagem */}
              {attachmentPreview && (
                <div>
                  <img
                    src={attachmentPreview}
                    alt="Preview"
                    style={{ width: 'auto', maxHeight: '80px' }}
                  />
                </div>
              )}
            </div>

            {/* Paste Area */}
            <div
              className="border border-gray-400 rounded-lg p-3 min-h-32 cursor-text bg-gray-50 flex items-center justify-center"
              onPaste={handlePaste}
              tabIndex={0}
              role="button"
            >
              <p className="text-sm text-gray-600 pointer-events-none">Cole uma imagem aqui</p>
            </div>

            {/* Botões de Ação */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleAddAttachment}
                disabled={creatingAttachment || !attachmentName.trim() || !attachmentDescription.trim() || !attachmentPreview}
              >
                {creatingAttachment && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Adicionar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setAttachmentModalOpen(false);
                  resetAttachmentForm();
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Edição de Atributos */}
      <Dialog open={editingSection !== null} onOpenChange={(open) => {
        if (!open) handleCancelEdit();
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar {editingSection}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {editingSection === 'nomeCompleto' && (
              <div>
                <Label htmlFor="fullName">Nome Completo</Label>
                <Input
                  id="fullName"
                  value={editingValues.fullName || ''}
                  onChange={(e) => handleAttributeChange('fullName', e.target.value)}
                  placeholder="Digite o nome completo"
                />
              </div>
            )}

            {editingSection === 'documentos' && (
              <>
                <div>
                  <Label htmlFor="rgDocument">RG</Label>
                  <Input
                    id="rgDocument"
                    value={editingValues.rgDocument || ''}
                    onChange={(e) => handleAttributeChange('rgDocument', e.target.value)}
                    placeholder="Digite o RG"
                  />
                </div>
                <div>
                  <Label htmlFor="cpfDocument">CPF</Label>
                  <Input
                    id="cpfDocument"
                    value={editingValues.cpfDocument || ''}
                    onChange={(e) => handleAttributeChange('cpfDocument', e.target.value)}
                    placeholder="Digite o CPF"
                  />
                </div>
              </>
            )}

            {editingSection === 'dadosCivicos' && (
              <>
                <div>
                  <Label htmlFor="scholarLevel">Escolaridade</Label>
                  <Input
                    id="scholarLevel"
                    value={editingValues.scholarLevel || ''}
                    onChange={(e) => handleAttributeChange('scholarLevel', e.target.value)}
                    placeholder="Digite a escolaridade"
                  />
                </div>
                <div>
                  <Label htmlFor="dateOfBirth">Data de Nascimento (DDMMYYYY)</Label>
                  <Input
                    id="dateOfBirth"
                    value={editingValues.dateOfBirth || ''}
                    onChange={(e) => handleAttributeChange('dateOfBirth', e.target.value)}
                    placeholder="Ex: 01011990"
                    maxLength={8}
                  />
                </div>
                <div>
                  <Label htmlFor="maritalStatus">Estado Civil</Label>
                  <Input
                    id="maritalStatus"
                    value={editingValues.maritalStatus || ''}
                    onChange={(e) => handleAttributeChange('maritalStatus', e.target.value)}
                    placeholder="Digite o estado civil"
                  />
                </div>
                <div>
                  <Label htmlFor="nationality">Nacionalidade</Label>
                  <Input
                    id="nationality"
                    value={editingValues.nationality || ''}
                    onChange={(e) => handleAttributeChange('nationality', e.target.value)}
                    placeholder="Digite a nacionalidade"
                  />
                </div>
              </>
            )}

            {editingSection === 'contatosSociais' && (
              <>
                <div>
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={editingValues.phone || ''}
                    onChange={(e) => handleAttributeChange('phone', e.target.value)}
                    placeholder="Digite o telefone"
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={editingValues.email || ''}
                    onChange={(e) => handleAttributeChange('email', e.target.value)}
                    placeholder="Digite o e-mail"
                  />
                </div>
                <div>
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input
                    id="instagram"
                    value={editingValues.instagram || ''}
                    onChange={(e) => handleAttributeChange('instagram', e.target.value)}
                    placeholder="Digite o usuário do Instagram"
                  />
                </div>
              </>
            )}

            {editingSection === 'localidade' && (
              <>
                <div>
                  <Label htmlFor="cityState">Cidade/Estado</Label>
                  <Input
                    id="cityState"
                    value={editingValues.cityState || ''}
                    onChange={(e) => handleAttributeChange('cityState', e.target.value)}
                    placeholder="Digite cidade e estado"
                  />
                </div>
                <div>
                  <Label htmlFor="neighborhood">Bairro</Label>
                  <Input
                    id="neighborhood"
                    value={editingValues.neighborhood || ''}
                    onChange={(e) => handleAttributeChange('neighborhood', e.target.value)}
                    placeholder="Digite o bairro"
                  />
                </div>
                <div>
                  <Label htmlFor="address">Endereço</Label>
                  <Input
                    id="address"
                    value={editingValues.address || ''}
                    onChange={(e) => handleAttributeChange('address', e.target.value)}
                    placeholder="Digite o endereço"
                  />
                </div>
                <div>
                  <Label htmlFor="cep">CEP</Label>
                  <Input
                    id="cep"
                    value={editingValues.cep || ''}
                    onChange={(e) => handleAttributeChange('cep', e.target.value)}
                    placeholder="Digite o CEP"
                  />
                </div>
              </>
            )}

            {editingSection === 'restricoesCredito' && (
              <>
                <div>
                  <Label htmlFor="cpfRestrictions">Restrição</Label>
                  <Input
                    id="cpfRestrictions"
                    value={editingValues.cpfRestrictions || ''}
                    onChange={(e) => handleAttributeChange('cpfRestrictions', e.target.value)}
                    placeholder="Digite a restrição"
                  />
                </div>
                <div>
                  <Label htmlFor="avalist">Avalista</Label>
                  <Input
                    id="avalist"
                    value={editingValues.avalist || ''}
                    onChange={(e) => handleAttributeChange('avalist', e.target.value)}
                    placeholder="Digite o avalista"
                  />
                </div>
                <div>
                  <Label htmlFor="creditCard">Cartão de Crédito</Label>
                  <Input
                    id="creditCard"
                    value={editingValues.creditCard || ''}
                    onChange={(e) => handleAttributeChange('creditCard', e.target.value)}
                    placeholder="Digite as informações de cartão"
                  />
                </div>
              </>
            )}

            {editingSection === 'parentalidade' && (
              <>
                <div>
                  <Label htmlFor="mothersName">Nome da Mãe</Label>
                  <Input
                    id="mothersName"
                    value={editingValues.mothersName || ''}
                    onChange={(e) => handleAttributeChange('mothersName', e.target.value)}
                    placeholder="Digite o nome da mãe"
                  />
                </div>
                <div>
                  <Label htmlFor="children">Filhos</Label>
                  <Input
                    id="children"
                    value={editingValues.children || ''}
                    onChange={(e) => handleAttributeChange('children', e.target.value)}
                    placeholder="Digite a quantidade de filhos"
                  />
                </div>
              </>
            )}

            {editingSection === 'residenciaProfissao' && (
              <>
                <div>
                  <Label htmlFor="residence">Tipo de Moradia</Label>
                  <Input
                    id="residence"
                    value={editingValues.residence || ''}
                    onChange={(e) => handleAttributeChange('residence', e.target.value)}
                    placeholder="Digite o tipo de moradia"
                  />
                </div>
                <div>
                  <Label htmlFor="residenceTime">Período na Moradia</Label>
                  <Input
                    id="residenceTime"
                    value={editingValues.residenceTime || ''}
                    onChange={(e) => handleAttributeChange('residenceTime', e.target.value)}
                    placeholder="Digite o período"
                  />
                </div>
                <div>
                  <Label htmlFor="job">Profissão</Label>
                  <Input
                    id="job"
                    value={editingValues.job || ''}
                    onChange={(e) => handleAttributeChange('job', e.target.value)}
                    placeholder="Digite a profissão"
                  />
                </div>
                <div>
                  <Label htmlFor="income">Renda Mensal</Label>
                  <Input
                    id="income"
                    value={editingValues.income || ''}
                    onChange={(e) => handleAttributeChange('income', e.target.value)}
                    placeholder="Digite a renda mensal"
                  />
                </div>
              </>
            )}

            {editingSection === 'motivacao' && (
              <div>
                <Label htmlFor="salesMotivation">Motivação para vender</Label>
                <Textarea
                  id="salesMotivation"
                  value={editingValues.salesMotivation || ''}
                  onChange={(e) => handleAttributeChange('salesMotivation', e.target.value)}
                  placeholder="Digite a motivação"
                  className="min-h-[100px]"
                />
              </div>
            )}

            {editingSection === 'objetivoLucro' && (
              <div>
                <Label htmlFor="profitObjective">Objetivo de Lucro/mês</Label>
                <Textarea
                  id="profitObjective"
                  value={editingValues.profitObjective || ''}
                  onChange={(e) => handleAttributeChange('profitObjective', e.target.value)}
                  placeholder="Digite o objetivo"
                  className="min-h-[100px]"
                />
              </div>
            )}

            {editingSection === 'duvidas' && (
              <div>
                <Label htmlFor="doubt">Dúvidas</Label>
                <Textarea
                  id="doubt"
                  value={editingValues.doubt || ''}
                  onChange={(e) => handleAttributeChange('doubt', e.target.value)}
                  placeholder="Digite as dúvidas"
                  className="min-h-[100px]"
                />
              </div>
            )}

            {editingSection === 'referencia1' && (
              <>
                <div>
                  <Label htmlFor="referenceNameOne">Nome</Label>
                  <Input
                    id="referenceNameOne"
                    value={editingValues.referenceNameOne || ''}
                    onChange={(e) => handleAttributeChange('referenceNameOne', e.target.value)}
                    placeholder="Digite o nome"
                  />
                </div>
                <div>
                  <Label htmlFor="referencePhoneOne">Telefone</Label>
                  <Input
                    id="referencePhoneOne"
                    value={editingValues.referencePhoneOne || ''}
                    onChange={(e) => handleAttributeChange('referencePhoneOne', e.target.value)}
                    placeholder="Digite o telefone"
                  />
                </div>
              </>
            )}

            {editingSection === 'referencia2' && (
              <>
                <div>
                  <Label htmlFor="referenceNameTwo">Nome</Label>
                  <Input
                    id="referenceNameTwo"
                    value={editingValues.referenceNameTwo || ''}
                    onChange={(e) => handleAttributeChange('referenceNameTwo', e.target.value)}
                    placeholder="Digite o nome"
                  />
                </div>
                <div>
                  <Label htmlFor="referencePhoneTwo">Telefone</Label>
                  <Input
                    id="referencePhoneTwo"
                    value={editingValues.referencePhoneTwo || ''}
                    onChange={(e) => handleAttributeChange('referencePhoneTwo', e.target.value)}
                    placeholder="Digite o telefone"
                  />
                </div>
              </>
            )}

            {editingSection === 'referenciaComercial' && (
              <>
                <div>
                  <Label htmlFor="referenceComercial">Nome</Label>
                  <Input
                    id="referenceComercial"
                    value={editingValues.referenceComercial || ''}
                    onChange={(e) => handleAttributeChange('referenceComercial', e.target.value)}
                    placeholder="Digite o nome"
                  />
                </div>
                <div>
                  <Label htmlFor="referenceComercialPhone">Telefone</Label>
                  <Input
                    id="referenceComercialPhone"
                    value={editingValues.referenceComercialPhone || ''}
                    onChange={(e) => handleAttributeChange('referenceComercialPhone', e.target.value)}
                    placeholder="Digite o telefone"
                  />
                </div>
              </>
            )}

            {editingSection === 'interesseXizinha' && (
              <div>
                <Label htmlFor="grauInteresse">Interesse utilizar Xizinha</Label>
                <Input
                  id="grauInteresse"
                  value={editingValues.grauInteresse || ''}
                  onChange={(e) => handleAttributeChange('grauInteresse', e.target.value)}
                  placeholder="Digite o grau de interesse"
                />
              </div>
            )}

            {editingSection === 'aprendizagemAplicativo' && (
              <div>
                <Label htmlFor="aprenderAplicativo">Aprendizado utilização do aplicativo</Label>
                <Input
                  id="aprenderAplicativo"
                  value={editingValues.aprenderAplicativo || ''}
                  onChange={(e) => handleAttributeChange('aprenderAplicativo', e.target.value)}
                  placeholder="Digite o aprendizado"
                />
              </div>
            )}

            {editingSection === 'usoTecnologia' && (
              <div>
                <Label htmlFor="usoTecnologia">Uso de tecnologia/redes sociais</Label>
                <Input
                  id="usoTecnologia"
                  value={editingValues.usoTecnologia || ''}
                  onChange={(e) => handleAttributeChange('usoTecnologia', e.target.value)}
                  placeholder="Digite o uso de tecnologia"
                />
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button
              variant="outline"
              onClick={handleCancelEdit}
              disabled={savingAttributes}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveAttributes}
              disabled={savingAttributes}
            >
              {savingAttributes && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract Creation Modal */}
      <Dialog open={contractModalOpen} onOpenChange={setContractModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Criar Contrato para Assinatura</DialogTitle>
            <DialogDescription>
              Deseja criar um contrato para que o usuário assine?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Após a aprovação, o cliente poderá assinar o contrato.
            </p>

            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="dontCreateContract"
                checked={dontDoContract}
                onChange={(e) => setDontDoContract(e.target.checked)}
                className="w-4 h-4 cursor-pointer"
              />
              <label htmlFor="dontCreateContract" className="cursor-pointer text-sm">
                Não criar contrato agora
              </label>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Nota:</strong> Se marcar "Não criar contrato", você NÃO poderá criá-lo posteriormente, salvo exceções via suporte.
              </p>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleConfirmApproval}
              disabled={updatingStatus}
              className="flex-1"
            >
              {updatingStatus && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Aprovar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setContractModalOpen(false);
                setPendingApprovalData(null);
                setDontDoContract(false);
              }}
              disabled={updatingStatus}
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dispatch Confirmation Dialog */}
      <Dialog open={dispatchConfirmOpen} onOpenChange={setDispatchConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Disparo Manual</DialogTitle>
            <DialogDescription>
              {dispatchData?.referenceType}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-900">
                <strong>Aviso:</strong> Prosseguir com essa ação fará a <strong>exclusão da fila de disparo</strong> para este número. A mensagem será enviada manualmente via WhatsApp.
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-1">Nome da Referência</p>
              <p className="font-semibold">{dispatchData?.referenceName}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-1">Número de Telefone</p>
              <p className="font-semibold">{dispatchData?.phoneNumber}</p>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleConfirmDispatch}
              disabled={sendingDispatch}
            >
              {sendingDispatch && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar Disparo
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDispatchConfirmOpen(false);
                setDispatchData(null);
              }}
              disabled={sendingDispatch}
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreCadastroDetail;
